<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import QRCode from 'qrcode';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const status = ref('');
const instanceName = ref('');
const qrCode = ref('');
const errorMessage = ref('');
const lastUpdatedAt = ref('');
const isLoadingStatus = ref(false);
const isConnecting = ref(false);
const isLoadingQr = ref(false);
const isRestarting = ref(false);
const isLoggingOut = ref(false);
const isClearingSession = ref(false);

const providerConfig = computed(() => props.inbox.provider_config || {});
const phoneNumber = computed(
  () => providerConfig.value.phone_number || props.inbox.phone_number || '-'
);
const displayInstanceName = computed(
  () => instanceName.value || providerConfig.value.instance_name || '-'
);
const normalizedStatus = computed(() => status.value || 'unknown');
const statusLabel = computed(() => {
  const statusMap = {
    connected: t('INBOX_MGMT.BAILEYS_CONNECTION.STATUS.CONNECTED'),
    disconnected: t('INBOX_MGMT.BAILEYS_CONNECTION.STATUS.DISCONNECTED'),
    waiting_qr: t('INBOX_MGMT.BAILEYS_CONNECTION.STATUS.WAITING_QR'),
    reconnecting: t('INBOX_MGMT.BAILEYS_CONNECTION.STATUS.RECONNECTING'),
    auth_error: t('INBOX_MGMT.BAILEYS_CONNECTION.STATUS.AUTH_ERROR'),
    not_configured: t('INBOX_MGMT.BAILEYS_CONNECTION.STATUS.NOT_CONFIGURED'),
    timeout: t('INBOX_MGMT.BAILEYS_CONNECTION.STATUS.UNAVAILABLE'),
    gateway_error: t('INBOX_MGMT.BAILEYS_CONNECTION.STATUS.UNAVAILABLE'),
    unknown: t('INBOX_MGMT.BAILEYS_CONNECTION.STATUS.UNKNOWN'),
  };

  return statusMap[normalizedStatus.value] || normalizedStatus.value;
});
const shouldShowQr = computed(
  () => qrCode.value && normalizedStatus.value === 'waiting_qr'
);

const setLastUpdated = () => {
  lastUpdatedAt.value = new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(new Date());
};

const responseData = response => response?.data || {};

const responseError = response => {
  const data = responseData(response);
  return data.error || data.message || '';
};

const requestError = error => {
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    t('INBOX_MGMT.BAILEYS_CONNECTION.ERROR.GENERIC')
  );
};

const updateFromResponse = response => {
  const data = responseData(response);
  status.value = data.status || status.value || 'unknown';
  instanceName.value = data.instance_name || instanceName.value;
  errorMessage.value = responseError(response);
  setLastUpdated();
  return data;
};

const qrDataUrlFromResponse = async response => {
  const data = responseData(response);
  if (data.qr_data_url) return data.qr_data_url;
  if (data.qr) return QRCode.toDataURL(data.qr);
  return '';
};

const refreshStatus = async () => {
  isLoadingStatus.value = true;
  errorMessage.value = '';

  try {
    const response = await InboxesAPI.getBaileysStatus(props.inbox.id);
    updateFromResponse(response);
  } catch (error) {
    errorMessage.value = requestError(error);
  } finally {
    isLoadingStatus.value = false;
  }
};

const refreshQr = async () => {
  isLoadingQr.value = true;
  errorMessage.value = '';

  try {
    const response = await InboxesAPI.getBaileysQr(props.inbox.id);
    updateFromResponse(response);
    qrCode.value = await qrDataUrlFromResponse(response);
    if (!qrCode.value && normalizedStatus.value !== 'connected') {
      errorMessage.value =
        responseError(response) ||
        t('INBOX_MGMT.BAILEYS_CONNECTION.ERROR.QR_NOT_READY');
    }
  } catch (error) {
    errorMessage.value = requestError(error);
  } finally {
    isLoadingQr.value = false;
  }
};

const connectWhatsapp = async () => {
  isConnecting.value = true;
  errorMessage.value = '';
  qrCode.value = '';

  try {
    const response = await InboxesAPI.connectBaileys(props.inbox.id);
    updateFromResponse(response);
    await refreshQr();
  } catch (error) {
    errorMessage.value = requestError(error);
  } finally {
    isConnecting.value = false;
  }
};

const restartConnection = async () => {
  isRestarting.value = true;
  errorMessage.value = '';
  qrCode.value = '';

  try {
    const response = await InboxesAPI.restartBaileys(props.inbox.id);
    updateFromResponse(response);
    useAlert(t('INBOX_MGMT.BAILEYS_CONNECTION.SUCCESS.RESTART'));
  } catch (error) {
    errorMessage.value = requestError(error);
  } finally {
    isRestarting.value = false;
  }
};

const logoutConnection = async () => {
  isLoggingOut.value = true;
  errorMessage.value = '';
  qrCode.value = '';

  try {
    const response = await InboxesAPI.logoutBaileys(props.inbox.id);
    updateFromResponse(response);
    useAlert(t('INBOX_MGMT.BAILEYS_CONNECTION.SUCCESS.LOGOUT'));
  } catch (error) {
    errorMessage.value = requestError(error);
  } finally {
    isLoggingOut.value = false;
  }
};

const clearSession = async () => {
  if (!window.confirm(t('INBOX_MGMT.BAILEYS_CONNECTION.CONFIRM_CLEAR_SESSION'))) {
    return;
  }

  isClearingSession.value = true;
  errorMessage.value = '';
  qrCode.value = '';

  try {
    const response = await InboxesAPI.clearBaileysSession(props.inbox.id);
    updateFromResponse(response);
    useAlert(t('INBOX_MGMT.BAILEYS_CONNECTION.SUCCESS.CLEAR_SESSION'));
  } catch (error) {
    errorMessage.value = requestError(error);
  } finally {
    isClearingSession.value = false;
  }
};

onMounted(refreshStatus);
</script>

<template>
  <div class="mx-6 max-w-4xl">
    <section class="flex flex-col gap-6">
      <div>
        <h2 class="text-xl font-medium text-n-slate-12 mb-1">
          {{ $t('INBOX_MGMT.BAILEYS_CONNECTION.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11 mb-0">
          {{ $t('INBOX_MGMT.BAILEYS_CONNECTION.DESCRIPTION') }}
        </p>
      </div>

      <div
        class="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-xl border border-n-weak p-4 bg-n-solid-1"
      >
        <div>
          <div class="text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.BAILEYS_CONNECTION.FIELDS.INSTANCE_NAME') }}
          </div>
          <div class="text-sm font-medium text-n-slate-12 break-all">
            {{ displayInstanceName }}
          </div>
        </div>
        <div>
          <div class="text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.BAILEYS_CONNECTION.FIELDS.PHONE_NUMBER') }}
          </div>
          <div class="text-sm font-medium text-n-slate-12">
            {{ phoneNumber }}
          </div>
        </div>
        <div>
          <div class="text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.BAILEYS_CONNECTION.FIELDS.STATUS') }}
          </div>
          <div class="text-sm font-medium text-n-slate-12">
            {{ statusLabel }}
          </div>
        </div>
        <div>
          <div class="text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.BAILEYS_CONNECTION.FIELDS.LAST_UPDATED') }}
          </div>
          <div class="text-sm font-medium text-n-slate-12">
            {{ lastUpdatedAt || '-' }}
          </div>
        </div>
      </div>

      <div
        v-if="errorMessage"
        class="rounded-xl border border-n-ruby-5 bg-n-ruby-2 text-n-ruby-11 px-4 py-3 text-sm"
      >
        {{ errorMessage }}
      </div>

      <div class="flex flex-wrap gap-2">
        <NextButton
          :label="$t('INBOX_MGMT.BAILEYS_CONNECTION.ACTIONS.REFRESH_STATUS')"
          icon="i-lucide-refresh-cw"
          slate
          faded
          :is-loading="isLoadingStatus"
          @click="refreshStatus"
        />
        <NextButton
          :label="$t('INBOX_MGMT.BAILEYS_CONNECTION.ACTIONS.CONNECT')"
          icon="i-lucide-link"
          blue
          :is-loading="isConnecting"
          @click="connectWhatsapp"
        />
        <NextButton
          :label="$t('INBOX_MGMT.BAILEYS_CONNECTION.ACTIONS.REFRESH_QR')"
          icon="i-lucide-qr-code"
          slate
          faded
          :is-loading="isLoadingQr"
          @click="refreshQr"
        />
        <NextButton
          :label="$t('INBOX_MGMT.BAILEYS_CONNECTION.ACTIONS.RESTART')"
          icon="i-lucide-rotate-cw"
          slate
          faded
          :is-loading="isRestarting"
          @click="restartConnection"
        />
        <NextButton
          :label="$t('INBOX_MGMT.BAILEYS_CONNECTION.ACTIONS.LOGOUT')"
          icon="i-lucide-unlink"
          slate
          faded
          :is-loading="isLoggingOut"
          @click="logoutConnection"
        />
        <NextButton
          :label="$t('INBOX_MGMT.BAILEYS_CONNECTION.ACTIONS.CLEAR_SESSION')"
          icon="i-lucide-trash-2"
          ruby
          faded
          :is-loading="isClearingSession"
          @click="clearSession"
        />
      </div>

      <div
        v-if="shouldShowQr"
        class="w-fit rounded-xl border border-n-weak bg-white p-4"
      >
        <img
          :src="qrCode"
          :alt="$t('INBOX_MGMT.BAILEYS_CONNECTION.QR_ALT')"
          class="size-64"
        />
      </div>
    </section>
  </div>
</template>
